/**
 * @ken/module-rates_and_units — frontend entry point (ADR-0276, SS-B5-T1).
 *
 * Units of measure and price levels moved here from Products, so a company
 * with Products switched off (a services company such as AquaFlow) still
 * reaches them. The screens are the ones Products had, unchanged apart from
 * their addresses, permission and words.
 *
 * The old addresses (/products/units-of-measure, /products/price-levels and
 * their detail pages) redirect here for one release (slice 1 R-6). They live
 * in this module, not in Products, so they work while Products is off.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

// Screens download the first time they are opened (ADR-040, BND-01).
const PriceLevelDetailPage = lazyScreen(
  () => import("./routes/price-level-detail"),
  "PriceLevelDetailPage",
);
const PriceLevelListPage = lazyScreen(
  () => import("./routes/price-level-list"),
  "PriceLevelListPage",
);
const UnitOfMeasureDetailPage = lazyScreen(
  () => import("./routes/unit-of-measure-detail"),
  "UnitOfMeasureDetailPage",
);
const UnitOfMeasureListPage = lazyScreen(
  () => import("./routes/unit-of-measure-list"),
  "UnitOfMeasureListPage",
);

/** POP-01: a list whose only search parameter is the record open in its pop-up. */
export interface RecordPopupSearch {
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

function popupSearch(raw: Record<string, unknown>): RecordPopupSearch {
  return {
    ...(typeof raw["open"] === "string" && raw["open"]
      ? { open: raw["open"] }
      : {}),
    ...(typeof raw["tab"] === "string" && raw["tab"]
      ? { tab: raw["tab"] }
      : {}),
  };
}

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const unitsOfMeasureRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "rates-and-units/units-of-measure",
    validateSearch: popupSearch,
    component: UnitOfMeasureListPage,
  });

  const unitOfMeasureDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "rates-and-units/units-of-measure/$id",
    component: UnitOfMeasureDetailPage,
  });

  const priceLevelsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "rates-and-units/price-levels",
    validateSearch: popupSearch,
    component: PriceLevelListPage,
  });

  const priceLevelDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "rates-and-units/price-levels/$id",
    component: PriceLevelDetailPage,
  });

  // ── Old addresses (R-6): literal segments, so they win over products/$id. ──
  const oldUnitsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "products/units-of-measure",
    validateSearch: popupSearch,
    beforeLoad: ({ params, search }) => {
      const { tenantSlug } = params as { tenantSlug: string };
      throw redirect({
        to: `/${tenantSlug}/rates-and-units/units-of-measure` as never,
        search: search as never,
      });
    },
  });

  const oldUnitDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "products/units-of-measure/$id",
    beforeLoad: ({ params }) => {
      const { tenantSlug, id } = params as { tenantSlug: string; id: string };
      throw redirect({
        to: `/${tenantSlug}/rates-and-units/units-of-measure/${id}` as never,
      });
    },
  });

  const oldPriceLevelsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "products/price-levels",
    validateSearch: popupSearch,
    beforeLoad: ({ params, search }) => {
      const { tenantSlug } = params as { tenantSlug: string };
      throw redirect({
        to: `/${tenantSlug}/rates-and-units/price-levels` as never,
        search: search as never,
      });
    },
  });

  const oldPriceLevelDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "products/price-levels/$id",
    beforeLoad: ({ params }) => {
      const { tenantSlug, id } = params as { tenantSlug: string; id: string };
      throw redirect({
        to: `/${tenantSlug}/rates-and-units/price-levels/${id}` as never,
      });
    },
  });

  return [
    unitsOfMeasureRoute,
    unitOfMeasureDetailRoute,
    priceLevelsRoute,
    priceLevelDetailRoute,
    oldUnitsRoute,
    oldUnitDetailRoute,
    oldPriceLevelsRoute,
    oldPriceLevelDetailRoute,
  ];
};
